import { Location } from 'history'
import { matchPath } from 'react-router'
import {
  authedFlyoutRoutePropsWithBackgroundLocations,
  BackgroundLocationStateBase,
  RoutePropsWithBackgroundLocation,
  unauthedRightFlyoutRoutePropsWithBackgroundLocations,
} from 'src/navigation/modalBackgrounds'

// Pure background-location math -- no `react-router-dom` import (only
// `react-router`'s route matching, no hooks) -- so non-React callers such as
// the Redux selector layer wired into `configureStore.ts` can use it without
// pulling React Router into the store bootstrap. `backgroundLocation.ts`
// re-exports these alongside the React-only `useBackgroundLocation` hook.

const routeMatchesLocation = (
  route: RoutePropsWithBackgroundLocation,
  location: Location,
): boolean =>
  Boolean(
    matchPath(location.pathname, route.routeProps) &&
      !route.exceptionRouteProps?.some((exceptionRouteProps) =>
        matchPath(location.pathname, exceptionRouteProps),
      ),
  )

export const findRouteBackgroundLocation = <
  S extends BackgroundLocationStateBase<S> | undefined,
>(
  location: Location<S>,
): Location<S> => {
  const matchedFlyout =
    authedFlyoutRoutePropsWithBackgroundLocations.find((route) =>
      routeMatchesLocation(route, location),
    ) ||
    unauthedRightFlyoutRoutePropsWithBackgroundLocations.find((route) =>
      routeMatchesLocation(route, location),
    )

  if (matchedFlyout === undefined) {
    return location
  }

  // `calculateDefaultBackgroundLocation` accepts `Location<unknown>`, so any
  // `Location<S>` flows in without a cast. Only the return needs an assertion,
  // narrowing back from `NavigationLocation` to the caller's `S` -- safe
  // because every route-table entry's state literal satisfies
  // `BackgroundLocationStateBase`, which is the only constraint `S` carries.
  return matchedFlyout.calculateDefaultBackgroundLocation(
    location,
  ) as unknown as Location<S>
}

export const computeBackgroundLocation = <
  S extends BackgroundLocationStateBase<S> | undefined,
>(
  location: Location<S>,
): Location<S> => {
  return location.state?.backgroundLocation
    ? location.state.backgroundLocation
    : findRouteBackgroundLocation(location)
}
